<script setup lang="ts">
import { fetchProductsData, fetchTicketCategoriesData, type InfoData, queryKey } from '~/queries'
import UiSelect from '@boats/ui/components/UiSelect.vue'
import UiAccordion from '@boats/ui/components/UiAccordion.vue'
import { cleanPath } from '~/utils'
import { computed } from 'vue'
import TicketDetailModal from '~/components/football/TicketDetailModal.vue'
import ViewBanner from '~/components/football/ViewBanner.vue'
import SeatPlan from '~/components/football/SeatPlan.vue'
import MatchList from '~/components/football/MatchList.vue'
import ClubInfoSection from '~/components/football/ClubInfoSection.vue'
import dayjs from '@/plugins/dayjs'
import { useJsonLdStore } from '~/stores/jsonld'
import { useRouter } from 'vue-router';

const props = defineProps<{
  breadcrumbs: InfoData['breadcrumbs']
  product: InfoData['product']
  refresh: () => Promise<unknown>
}>()

const { $i18n, $analyticsManager } = useNuxtApp()
const { t } = $i18n

const site = useSiteStore()
const cart = useCartStore()
const modals = useModalsStore()
const router = useRouter();

const loading = ref(false)

// Storefront view (BOAT-1912): `?view=<slug>` shows only this view's ticket
// categories, the partner logo in the hero and a banner back to the full page.
// The param is carried into the other-fixtures list; nothing is persisted.
const { activeView, offeredViews, viewQuery, hrefForView, exitHref } = useStorefrontView(() => props.product?.views)

const jsonStore = useJsonLdStore()
jsonStore.addSportEvent(props.product, site)

const breadcrumbsItems = computed(() =>
  props.breadcrumbs.map((item) => ({
    title: item.title,
    href: cleanPath('/', item.path)
  }))
)

const goPrevBreadcrumb = () => {
  const list = breadcrumbsItems.value
  if (list.length > 1) {
    router.push(list[list.length - 2].href)
  }
}

const { data: ticketCategoriesData, status: fetchStatus } = await useAsyncData(
  queryKey('ticket-categories', props.product?.id, activeView.value?.slug ?? null),
  async () => {
    if (!props.product?.id) return

    return await fetchTicketCategoriesData({
      productId: props.product.id,
      view: activeView.value?.slug ?? null,
    })
  }
)
const isFetched = computed(() => !(fetchStatus.value === 'pending' || fetchStatus.value === 'idle'))

const categories = computed(() => ticketCategoriesData.value?.items || [])

const productCompetition = ref(props.product?.competitions?.at(0)?.name)

const maximumNumberOfTickets = ref(
  Math.max(...categories.value.map(item => item.maxQuantity || 0))
)

const hasTickets = computed(() => {
  if (!categories.value?.length) return false
  if (!props.product.startDate) return true

  const tz = props.product?.venue?.timezone || 'UTC'
  let kickoff = dayjs.tz(props.product.startDate, tz).startOf('day')
  const timeStr = props.product.startTime
  if (timeStr) {
    const formats = ['HH:mm:ss', 'HH:mm']
    let parsed: dayjs.Dayjs | null = null
    for (const f of formats) {
      const t = dayjs(timeStr, f, true)
      if (t.isValid()) {
        parsed = t
        break
      }
    }
    if (parsed) {
      kickoff = kickoff
        .hour(parsed.hour())
        .minute(parsed.minute())
        .second(parsed.second())
        .millisecond(0)
    }
  }

  const now = dayjs().tz(tz)
  return now.isBefore(kickoff)
})

const selectedTicketCountDefault = Math.min(2, maximumNumberOfTickets.value)

const selectedTicketCount = ref({
  label: selectedTicketCountDefault + ' ' + t('string.tickets'),
  value: selectedTicketCountDefault
})

const selectedTicketCategory = ref(null)
const selectedLocation = ref(null)

// Clubs configured as mandatory-hotel in the CMS send the customer straight into
// the hotel search instead of the plain cart review — see BOAT-1755. A ticket
// category can override the club, and its value already has the club folded in,
// so it wins as soon as one is selected.
const mandatoryHotel = computed(
  () => selectedTicketCategory.value?.mandatoryHotel ?? !!props.product?.mandatoryHotel,
)

const ticketsCount = ref(
  Array.from({ length: maximumNumberOfTickets.value }, (_, i) => i + 1)
)

const seatLocations = ref(categories.value.map(item => ({
    label: item.title,
    value: item.id
  })
))

const ticketsPrice = computed(() => {
  return selectedTicketCount.value.value * selectedTicketCategory.value.price
})

// Price-drop (BOAT-1738): the reference total and money saved for the selected
// quantity. Both null unless the API flagged a genuine drop on this category.
const ticketsWasPrice = computed(() => {
  const category = selectedTicketCategory.value
  if (!category?.wasPrice || category.wasPrice <= category.price) return null

  return selectedTicketCount.value.value * category.wasPrice
})

const ticketsSaved = computed(() =>
  ticketsWasPrice.value === null
    ? null
    : ticketsWasPrice.value - ticketsPrice.value,
)

const faqs = ref([])

if (props.product?.body) {
  faqs.value.push({
    title: t('page.product.additionalDescription.title'),
    content: props.product.body
  })
} else if (props.product?.additionalDescription) {
  faqs.value.push({
    title: t('page.product.additionalDescription.title'),
    content: props.product.additionalDescription
  })
}

if (props.product?.notes) {
  faqs.value.push({
    title: t('page.product.additionalNotes.title.football'),
    content: props.product.notes
  })
} else if (props.product?.additionalNotes) {
  faqs.value.push({
    title: t('page.product.additionalNotes.title.football'),
    content: props.product.additionalNotes
  })
}

const { openSidebar, closeSidebar, isSidebarOpened } = useSidebar()

const selectCategory = async (category) => {
  selectedTicketCategory.value = category
  await nextTick()
  openSidebar()
}

const seatPlan = ref(props.product?.venue?.seatPlans?.find(plan => plan.data.type === 'football_match'))
const seatPlanCategories = computed(() => {
  const ids = new Set(categories.value.map(c => c.id))
  return seatPlan.value?.categories?.filter(cat => ids.has(cat.data.ticket_category_id)) || []
})
const svgUrl = seatPlan.value?.media[0]?.data?.url

const seatPlanMedia = ref({
  hasSvg: typeof svgUrl === 'string' && svgUrl.trim().toLowerCase().endsWith('.svg'),
  source: svgUrl || props.product?.venue?.stadiumImage
})

// Per-category Key Feature Labels (BOAT-1904). The card is tight on mobile, so
// show one chip there and the full set from tablet up (MatchList precedent);
// the buy sidebar and the details modal always show them all.
const isMobileViewport = ref(false)
onMounted(() => {
  isMobileViewport.value = window.innerWidth < 768
})
const limitedKeyFeatureLabels = (category) => {
  const labels = category?.keyFeatureLabels || []
  return isMobileViewport.value ? labels.slice(0, 1) : labels
}

const showModal = ref(false)
const openTicketDetails = (category) => {
  showModal.value = true
  selectedTicketCategory.value = category
}

const hoveredCategory = ref(null)
const pinnedCategory = ref(null)

function pinCategory(category) {
  if (category.id === pinnedCategory.value?.id) {
    pinnedCategory.value = null
  } else {
    pinnedCategory.value = category
  }
}

function hoverCategory(category) {
  hoveredCategory.value = category
}

function onHoverCategory(categoryId) {
  if (categoryId) {
    hoveredCategory.value = categories.value.find(item => item.id === categoryId)
  } else {
    hoveredCategory.value = null
  }
}

function onPinCategory(categoryId) {
  if (categoryId === pinnedCategory.value?.id) {
    pinnedCategory.value = null
  } else {
    pinnedCategory.value = categories.value.find(item => item.id === categoryId)
  }
}

const productsPerPage = 4
const productsByPage = reactive({})

function getFootballClubPagesProducts(footballClubPages) {
  if (!footballClubPages?.length) return

  footballClubPages.forEach(page => {
    const key = `products-${page.id}`

    // Wrap in nextTick to ensure hydration
    nextTick(() => {
      const { data, status } = useAsyncData(key, () =>
        fetchProductsData({
          limit: productsPerPage,
          offset: 0,
          'search[current]': 1,
          'search[page-descendants]': page.id,
          'search[exclude-ids]': props.product.id,
          ...(activeView.value ? { 'search[view]': activeView.value.slug } : {}),
          'ignoreCount': 1,
          orderByRaw: 'start_date,ASC'
        })
      )

      productsByPage[page.id] = {
        data,
        status,
        title: page.title,
        description: page.description
      }
    })
  })
}

onMounted(() => {
  $analyticsManager.trackProduct(props.product)
  getFootballClubPagesProducts(props.product?.footballClubPages)
  if (categories.value?.length > 0) {
    selectedTicketCategory.value = {
      label: categories.value[0].title,
      value: categories.value[0].id
    }
  }
})
</script>

<template>
  <div class="football-product">
    <section class="event-info" :class="{ 'opened': isSidebarOpened }"
             :style="{'--seat-plan-bg-color': seatPlanMedia.hasSvg ? '#f4f4f4' : '#fff'}">
      <div class="event-info__sidebar-header only-mobile">
        <div class="event-info-card">
          <AppBreadcrumbs class="club-overview__breadcrumb only-desktop" :items="breadcrumbsItems" />
          <div class="event-info-card__title-row">
            <h1 class="event-info-card__title">{{ product.title }}</h1>
            <img
              v-if="activeView?.logoUrl"
              :src="activeView.logoUrl"
              :alt="activeView.name || ''"
              class="event-info-card__partner-logo"
            />
          </div>
          <span class="event-info-competition">{{productCompetition}}</span>
          <div class="event-info-card__guarantees">
            <svg width="17" height="19" viewBox="0 0 17 19" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M0 3.39267V9.5C0 16.0077 7.61583 18.8442 7.94281 18.9596C8.09247 19.0135 8.25636 19.0135 8.40603 18.9596C8.733 18.8442 16.3488 16.0077 16.3488 9.5V3.39267C16.3488 3.25321 16.3056 3.11716 16.2251 3.00302C16.1447 2.88888 16.0309 2.80221 15.8992 2.7548L8.40603 0.0404268C8.25636 -0.0134756 8.09247 -0.0134756 7.94281 0.0404268L0.449593 2.7548C0.317937 2.80221 0.204143 2.88888 0.123697 3.00302C0.0432511 3.11716 5.9564e-05 3.25321 0 3.39267Z"
                fill="#408A13" />
              <path fill-rule="evenodd" clip-rule="evenodd"
                    d="M11.9607 6.16386L7.22431 11.5228L4.85791 9.21466L5.74234 8.30789L7.15631 9.68703L11.0116 5.32501L11.9607 6.16386Z"
                    fill="white" />
            </svg>
            <span class="club-overview__guarantees-card-title"><span
              class="event-info-card__guarantees-bold-title">100<span
              class="event-info-card__guarantees-percent">%</span> {{ $t('page.category-football.guarantee.official') }}</span> {{ $t('page.category-football.guarantee.authorised-tickets')
              }}</span>
          </div>
          <div class="event-info-card__time-and-location">
            <div>
              <div class="event-info-card__time-and-location-field field-1">
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M3 1.2002H9V0H10.2002V1.2002H12V12H0V1.2002H1.7998V0H3V1.2002ZM1.2002 10.7998H10.7998V5.40039H1.2002V10.7998ZM4.2002 9.59961H2.40039V7.7998H4.2002V9.59961Z"
                    fill="#5A5A5A" />
                </svg>
                <span class="event-info-card__time-and-location-text">{{ formatDate(product.startDate) }}</span>
              </div>
              <div class="event-info-card__time-and-location-field field-2">
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M8.48027 0L12 3.62051V8.72227L8.47969 12H3.51973L0 8.72227V3.62051L3.51973 0H8.48027ZM1.2 4.10742V8.19961L3.99199 10.8H8.00801L10.8 8.19961V4.10742L7.97285 1.2H4.02715L1.2 4.10742Z"
                    fill="#5A5A5A" />
                  <path d="M6.60002 2.40002V6.07444L8.55178 8.3051L7.64827 9.09495L5.40002 6.52561V2.40002H6.60002Z"
                        fill="#5A5A5A" />
                </svg>
                <span
                  class="event-info-card__time-and-location-text">{{ (product.startTime && product.startTime) ? formatTime([product.startDate, product.startTime].join(' ')): 'TBD' }}</span>
              </div>
            </div>
            <div class="event-info-card__time-and-location-field field-3">
              <a :href="product?.venue.googleMapsLink || undefined" class="event-info-card-link card-link">
                <div class="event-info__location">
                  <svg width="12" height="13" viewBox="0 0 12 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M7.98394 0.5L10.8 3.02363V6.57969L6.1189 12.5L1.19995 6.57969V3.02363L4.01597 0.5H7.98394ZM2.39995 3.55918V6.14609L6.09546 10.5945L9.59995 6.16191V3.55918L7.52515 1.7H4.47476L2.39995 3.55918Z"
                      fill="#5A5A5A" />
                    <ellipse cx="6.00005" cy="5.29998" rx="1.2" ry="1.2" fill="#5A5A5A" />
                  </svg>
                  <span class="event-info-card__time-and-location-text">{{ product?.venue.name }}</span>
                </div>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M14.583 10.0044L7.7168 17.0679L6.2832 15.6733L11.793 10.0044L6.2832 4.33545L7.7168 2.94092L14.583 10.0044Z"
                    fill="#ffffff" />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </div>
      <div class="event-info__map">
        <div class="event-info__map-title">{{ product?.venue.name }}</div>
        <div class="event-info__map-canvas">
          <SeatPlan key="two"
                    :media="seatPlanMedia"
                    :categories="seatPlanCategories"
                    :active-category="hoveredCategory || pinnedCategory"
                    @hover="onHoverCategory"
                    @pin="onPinCategory"
          />
        </div>
      </div>
      <div class="event-info__sidebar" :class="{ 'opened': isSidebarOpened }">
        <div class="event-info__sidebar-header only-desktop">
          <div class="event-info-card">
            <AppBreadcrumbs class="club-overview__breadcrumb" :items="breadcrumbsItems" :ignoreJsonLd="true"/>
            <div class="event-info-card__title-row">
            <h1 class="event-info-card__title">{{ product.title }}</h1>
            <img
              v-if="activeView?.logoUrl"
              :src="activeView.logoUrl"
              :alt="activeView.name || ''"
              class="event-info-card__partner-logo"
            />
          </div>
            <span class="event-info-competition">{{productCompetition}}</span>
            <div class="event-info-card__guarantees">
              <svg width="17" height="19" viewBox="0 0 17 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M0 3.39267V9.5C0 16.0077 7.61583 18.8442 7.94281 18.9596C8.09247 19.0135 8.25636 19.0135 8.40603 18.9596C8.733 18.8442 16.3488 16.0077 16.3488 9.5V3.39267C16.3488 3.25321 16.3056 3.11716 16.2251 3.00302C16.1447 2.88888 16.0309 2.80221 15.8992 2.7548L8.40603 0.0404268C8.25636 -0.0134756 8.09247 -0.0134756 7.94281 0.0404268L0.449593 2.7548C0.317937 2.80221 0.204143 2.88888 0.123697 3.00302C0.0432511 3.11716 5.9564e-05 3.25321 0 3.39267Z"
                  fill="#408A13" />
                <path fill-rule="evenodd" clip-rule="evenodd"
                      d="M11.9607 6.16386L7.22431 11.5228L4.85791 9.21466L5.74234 8.30789L7.15631 9.68703L11.0116 5.32501L11.9607 6.16386Z"
                      fill="white" />
              </svg>
              <span class="club-overview__guarantees-card-title"><span class="event-info-card__guarantees-bold-title">100<span
                class="event-info-card__guarantees-percent">%</span> {{ $t('page.category-football.guarantee.official')
                }}</span> {{ $t('page.category-football.guarantee.authorised-tickets') }}</span>
            </div>
            <div class="event-info-card__time-and-location">
              <div>
                <div class="event-info-card__time-and-location-field field-1">
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M3 1.2002H9V0H10.2002V1.2002H12V12H0V1.2002H1.7998V0H3V1.2002ZM1.2002 10.7998H10.7998V5.40039H1.2002V10.7998ZM4.2002 9.59961H2.40039V7.7998H4.2002V9.59961Z"
                      fill="#5A5A5A" />
                  </svg>
                  <span class="event-info-card__time-and-location-text">{{ formatDate(product.startDate) }}</span>
                </div>
                <div class="event-info-card__time-and-location-field field-2">
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M8.48027 0L12 3.62051V8.72227L8.47969 12H3.51973L0 8.72227V3.62051L3.51973 0H8.48027ZM1.2 4.10742V8.19961L3.99199 10.8H8.00801L10.8 8.19961V4.10742L7.97285 1.2H4.02715L1.2 4.10742Z"
                      fill="#5A5A5A" />
                    <path d="M6.60002 2.40002V6.07444L8.55178 8.3051L7.64827 9.09495L5.40002 6.52561V2.40002H6.60002Z"
                          fill="#5A5A5A" />
                  </svg>
                  <span
                    class="event-info-card__time-and-location-text">{{ (product.startTime && product.startTime) ? formatTime([product.startDate, product.startTime].join(' ')): 'TBD' }}</span>
                </div>
              </div>
              <div class="event-info-card__time-and-location-field field-3">
                <a :href="product?.venue.googleMapsLink || undefined" class="event-info-card-link card-link">
                  <div class="event-info__location">
                    <svg width="12" height="13" viewBox="0 0 12 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M7.98394 0.5L10.8 3.02363V6.57969L6.1189 12.5L1.19995 6.57969V3.02363L4.01597 0.5H7.98394ZM2.39995 3.55918V6.14609L6.09546 10.5945L9.59995 6.16191V3.55918L7.52515 1.7H4.47476L2.39995 3.55918Z"
                        fill="#5A5A5A" />
                      <ellipse cx="6.00005" cy="5.29998" rx="1.2" ry="1.2" fill="#5A5A5A" />
                    </svg>
                    <span class="event-info-card__time-and-location-text">{{ product?.venue.name }}</span>
                  </div>
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M14.583 10.0044L7.7168 17.0679L6.2832 15.6733L11.793 10.0044L6.2832 4.33545L7.7168 2.94092L14.583 10.0044Z"
                      fill="#ffffff" />
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </div>
        <div v-if="!isSidebarOpened" class="event-info__tickets">
          <!-- Storefront views (BOAT-1912) -->
          <div v-if="offeredViews.length || activeView" class="event-info__view-banners">
            <ViewBanner
              v-for="view in offeredViews"
              :key="view.slug"
              :title="view.bannerTitle"
              :body="view.bannerBody"
              :cta="view.bannerCta || view.name || view.slug"
              :colour="view.colour"
              :href="hrefForView(view)"
            />
            <ViewBanner
              v-if="activeView"
              exit
              :title="activeView.name"
              :cta="$t('string.View-All-Tickets')"
              :href="exitHref"
            />
          </div>
          <div v-if="categories.length > 0" class="event-info__tickets-filters">
            <div class="dropdown-container">
              <UiSelect
                v-model="selectedTicketCount"
                :title="$t('component.tickets.show.quantity.label')"
                class="relative"
                :options="ticketsCount.map((item) => ({
                  value: item,
                  label: `${item} ${item > 1 ? $t('string.tickets') : $t('string.ticket')}`,
                }))"
              />
            </div>
            <div v-if="false" class="dropdown-container">
              <UiSelect
                v-model="selectedLocation"
                :title="$t('string.All-Locations')"
                class="relative"
                :options="[
                        { label: $t('string.All-Locations'), value: undefined },
                        ...seatLocations
                ]"
              />
            </div>
          </div>
          <template v-if="hasTickets">
            <ul class="event-info__tickets-list">
              <li v-for="category in categories"
                  :key="category.id"
                  class="event-info__tickets-item"
                  @mouseover="hoverCategory(category)"
                  @mouseleave="hoverCategory(null)"
                  @click="pinCategory(category)"
              >
                <div
                  :class="['event-info-tickets-card', { 'pinned-category': pinnedCategory?.id === category.id}, {'is-hovered': hoveredCategory?.id === category.id}]">
                  <div class="event-info-tickets-card-color-indicator"
                       :style="{ backgroundColor: category.colour }"></div>
                  <div class="event-info-tickets-card__info">
                    <div>
                      <span class="event-info-tickets-card__title">{{ category.title }}</span>
                      <div v-if="category.keyFeatureLabels?.length" class="event-info-tickets-card__labels">
                        <KeyLabelFeature
                          v-for="(label, i) in limitedKeyFeatureLabels(category)"
                          :key="'category-label-' + category.id + '-' + i"
                          :name="label.name"
                          :colour="label.colour"
                        />
                      </div>
                      <div class="event-info-tickets-card__location-wrapper">
                        <span class="event-info-tickets-card__location"></span>
                      </div>
                    </div>
                    <div class="flex-wrap">
                      <button class="event-info-tickets-card__details-button" @click="openTicketDetails(category)">
                        <span>{{ $t('string.Ticket-Details') }}</span>
                        <svg width="25" height="25" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <rect x="1.0625" y="1" width="23" height="23" rx="11.5" fill="white" />
                          <rect x="1.0625" y="1" width="23" height="23" rx="11.5" stroke="#121318" />
                          <path d="M10.1625 6.09998L16.5625 12.5L10.1625 18.9" stroke="#121318" stroke-width="1.5"
                                stroke-miterlimit="10" />
                        </svg>
                      </button>
                      <span v-if="category.maxQuantity == 2"
                            class="tic-info-tag selling-fast">2 {{ $t('string.Tickets-Left') }}</span>
                      <span v-if="category.maxQuantity == 1"
                            class="tic-info-tag selling-fast">{{ $t('string.Final-Ticket') }}</span>
                    </div>
                  </div>
                  <div class="event-info-tickets-card__buy">
                    <div class="event-info-tickets-card__buy-prices">
                      <span class="event-info-tickets-card__buy-price">{{ formatCurrency(category.price) }}</span>
                      <span v-if="category.wasPrice && category.wasPrice > category.price"
                            class="event-info-tickets-card__buy-was-price">{{ formatCurrency(category.wasPrice) }}</span>
                    </div>
                    <PriceDropBadge v-if="category.wasPrice && category.wasPrice > category.price"
                                    :saved="category.wasPrice - category.price"
                                    class="event-info-tickets-card__buy-save" />
                    <button class="event-info-tickets-card__buy-button tic-btn"
                            :disabled="selectedTicketCount.value > category.maxQuantity"
                            @click="selectCategory(category)">{{ $t('string.Buy').toUpperCase() }}
                    </button>
                  </div>
                </div>
              </li>
            </ul>
          </template>
          <div v-if="!hasTickets && isFetched" class="p-3 pt-6 md:p-6">
            <div v-if="product?.soldOut?.message" class="pb-4 reset-list-styles" v-html="product.soldOut.message"/>
            <p
              v-else
              v-html="$t('page.product.unavailable.description', {
                email: site.data?.emailAddress,
              })"
            />
            <AppButton full-width v-if="product?.soldOut?.modal" @click="modals.openContactUsModal()">{{ $t('page.faqs.extra.contact.title') }}</AppButton>
            <AppButton class="mt-3" full-width variant="outlined" @click="goPrevBreadcrumb()">{{ $t('string.Back-To-Category') }}</AppButton>
          </div>
        </div>
        <div v-if="selectedTicketCategory" id="buy-now-sidebar" class="buy-now__sidebar sidebar">
          <div class="buy-now__container">
            <div class="buy-now__container-header">
              <button class="buy-now__back-button" @click="closeSidebar">
                <svg width="26" height="26" viewBox="0 0 26 26" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="-0.5" y="0.5" width="25" height="25" rx="12.5" transform="matrix(-1 0 0 1 25 0)"
                        fill="white" />
                  <rect x="-0.5" y="0.5" width="25" height="25" rx="12.5" transform="matrix(-1 0 0 1 25 0)"
                        stroke="#121318" />
                  <path
                    d="M8.41699 13.0044L15.2832 20.0679L16.7168 18.6733L11.207 13.0044L16.7168 7.33545L15.2832 5.94092L8.41699 13.0044Z"
                    fill="#121318" />
                </svg>
                <span>{{ $t('string.Back') }}</span>
              </button>
              <span class="buy-now__header-club-vs-club">{{ product.title }}</span>
              <span class="buy-now__header-title">{{ selectedTicketCategory.title }}</span>
              <div v-if="selectedTicketCategory.keyFeatureLabels?.length" class="buy-now__header-labels">
                <KeyLabelFeature
                  v-for="(label, i) in selectedTicketCategory.keyFeatureLabels"
                  :key="'selected-label-' + i"
                  :name="label.name"
                  :colour="label.colour"
                />
              </div>
              <!--              <span class="buy-now__header-seats-info">West stand, Row 23, Seats 10-11</span>-->
            </div>
            <div class="event-info__map-canvas buy-now__map-canvas only-mobile">
              <SeatPlan key="one"
                        :media="seatPlanMedia"
                        :categories="seatPlanCategories"
                        :active-category="hoveredCategory || pinnedCategory"
                        @hover="onHoverCategory"
                        @pin="onPinCategory"
              />
            </div>
            <div class="buy-now__cards">
              <div class="buy-now__card buy-now__price tic-card">
                <div class="dropdown-container">
                  <UiSelect
                    v-model="selectedTicketCount"
                    :title="$t('component.tickets.show.quantity.label')"
                    class="relative"
                    :options="ticketsCount
                      .map((item) => ({
                        value: item,
                        label: `${item} ${item > 1 ? $t('string.tickets') : $t('string.ticket')}`,
                      }))
                      .filter((item) => item.value <= selectedTicketCategory.maxQuantity)"
                  />
                </div>
                <div class="buy-now__price-block">
                  <span>{{ $t('string.Tickets-Price') }}</span>
                  <div class="buy-now__price-amounts">
                    <span class="event-info-tickets-card__buy-price">{{ formatCurrency(ticketsPrice) }}</span>
                    <span v-if="ticketsWasPrice !== null" class="buy-now__price-was">{{ formatCurrency(ticketsWasPrice) }}</span>
                  </div>
                  <PriceDropBadge v-if="ticketsSaved !== null" :saved="ticketsSaved" class="buy-now__price-save" />
                </div>
              </div>
              <div class="buy-now__card buy-now__services tic-card">
                <ul class="buy-now__services-list">
                  <li class="buy-now__services-item">
                    <div class="buy-now__services">
                      <svg width="27" height="22" viewBox="0 0 27 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <g clip-path="url(#clip0_5258_35041)">
                          <path d="M21.9397 3.5L25.0403 6.44824V16.0566L21.9436 18.9697H19.72V3.5H21.9397Z"
                                stroke="#121318" stroke-width="2" />
                          <path
                            d="M16.7197 3.5V18.9805H1V15.791C3.09205 15.3329 4.66005 13.4691 4.66016 11.2402C4.66016 9.01124 3.09218 7.1466 1 6.68848V3.5H16.7197Z"
                            stroke="#121318" stroke-width="2" />
                        </g>
                        <defs>
                          <clipPath id="clip0_5258_35041">
                            <rect width="26.04" height="17.47" fill="white" transform="translate(0 2.5)" />
                          </clipPath>
                        </defs>
                      </svg>
                      <span>{{ $t('page.category-football.e-tickets') }}</span>
                    </div>
                  </li>
                  <li class="buy-now__services-item">
                    <div class="buy-now__services">
                      <svg width="20" height="14" viewBox="0 0 20 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <g clip-path="url(#clip0_5259_47710)">
                          <path
                            d="M19.0523 7.40546V1.54141L17.7558 0H12.8003L11.4709 1.53471V7.40546H10.4179L10.3784 9.49641L11.8526 10.9976H14.5311V12.6596H5.46232V10.9976H8.34485L9.60842 9.4629V7.39876H8.66074V1.54141L7.36426 0H2.41527L1.09246 1.53471V7.40546H0.0394867L0 9.49641L1.47417 10.9976H4.15268V12.6596H1.75058V14H18.5982V12.6596H15.8539V10.9976H18.7364L20 9.4629V7.39876H19.0523V7.40546ZM2.40869 2.04404L3.01415 1.34035H6.76538L7.3511 2.03734V7.40546H2.40869V2.04404ZM1.32938 8.95357V8.75251H8.30536V8.98708L7.74597 9.66395H2.0204L1.3228 8.95357H1.32938ZM12.7937 2.04404L13.3991 1.34035H17.1504L17.7361 2.03734V7.40546H12.7937V2.04404ZM18.6838 8.98037L18.1244 9.65725H12.4054L11.7144 8.94687V8.74581H18.6904V8.98037H18.6838Z"
                            fill="#121318" />
                        </g>
                        <defs>
                          <clipPath id="clip0_5259_47710">
                            <rect width="20" height="14" fill="white" />
                          </clipPath>
                        </defs>
                      </svg>
                      <span>{{ $t('page.category-football.seat-together') }}</span>
                    </div>
                  </li>
                  <li class="buy-now__services-item">
                    <div class="buy-now__services">
                      <svg width="15" height="16" viewBox="0 0 15 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <g clip-path="url(#clip0_5259_47712)">
                          <path
                            d="M13.9568 6.32184H13.4115V3.11877L10.1949 0H4.63909L1.37513 3.16475V6.32184H0.995785L0 7.28736V11.1341L1.0432 12.1456H3.94362V6.32184H2.95574V3.80077L5.29505 1.53257H9.53899L11.8309 3.75479V6.32184H11.0564V12.1456H12.1549L11.7914 13.3027L10.519 14.2222H8.92255V13.5862H5.12908V16H8.92255V15.3716H10.9062L12.8109 14L13.3957 12.1456H14.0121L15 11.1877V7.341L13.9568 6.32184Z"
                            fill="#121318" />
                        </g>
                        <defs>
                          <clipPath id="clip0_5259_47712">
                            <rect width="15" height="16" fill="white" />
                          </clipPath>
                        </defs>
                      </svg>
                      <span>{{ $t('page.category-football.customer-support') }}</span>
                    </div>
                  </li>
                </ul>
              </div>
              <div class="buy-now__card tic-card">
                    <span class="buy-now__ticket-details-title">
                        {{ $t('string.Ticket-Details') }}
                    </span>
                <div class="buy-now__ticket-details-content">
                  <AppPreviewContent :key="selectedTicketCategory.id">
                    <div class="clean-cms-styles" v-html="selectedTicketCategory.description"></div>
                  </AppPreviewContent>
                </div>
              </div>
              <div class="buy-now__card tic-card buy-now__guarantees">
                <svg width="22" height="26" viewBox="0 0 22 26" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M0 4.53203V12.664C0 21.3291 10.1406 25.106 10.576 25.2596C10.7752 25.3313 10.9935 25.3313 11.1927 25.2596C11.6281 25.106 21.7687 21.3291 21.7687 12.664V4.53203C21.7686 4.34634 21.7111 4.16518 21.604 4.01321C21.4969 3.86124 21.3453 3.74583 21.17 3.68269L11.1927 0.0684773C10.9935 -0.00329451 10.7752 -0.00329451 10.576 0.0684773L0.598639 3.68269C0.423337 3.74583 0.271819 3.86124 0.164704 4.01321C0.0575893 4.16518 7.93102e-05 4.34634 0 4.53203Z"
                    fill="#408A13" />
                  <path fill-rule="evenodd" clip-rule="evenodd"
                        d="M15.9275 8.22192L9.62086 15.3574L6.46997 12.2841L7.6476 11.0767L9.53031 12.9131L14.6637 7.10498L15.9275 8.22192Z"
                        fill="white" />
                </svg>

                <div>
                  <span class="club-overview__guarantees-card-title"><span
                    class="event-info-card__guarantees-bold-title">100<span class="event-info-card__guarantees-percent">%</span> {{ $t('page.category-football.guarantee.official')
                    }}</span><br />{{ $t('page.category-football.guarantee.authorised-tickets') }}</span>
                  <p class="club-overview__location-description">
                    We have a 30 year relationship of supplying tickets for all European football matches. You are in
                    safe hands.
                  </p>
                </div>
              </div>
              <div class="buy-now__card tic-card buy-now__guarantees">
                <svg width="22" height="26" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path fill-rule="evenodd" clip-rule="evenodd"
                        d="M10.0298 2.77579V8.75H17.3138L16.516 17.5H7.52234V6.94246L10.0298 2.77579ZM5.03941 6.25C5.05441 6.19816 5.07614 6.14824 5.10422 6.10155L8.59339 0.303438C8.70673 0.115188 8.91083 0 9.13099 0H11.9104C12.2565 0 12.5372 0.279825 12.5372 0.625V6.25H19.3731C19.7417 6.25 20.0307 6.56558 19.9974 6.93159L18.8576 19.4316C18.8283 19.7535 18.5576 20 18.2334 20H6.89547H5.64175H0.626861C0.280658 20 0 19.7201 0 19.375V6.875C0 6.52983 0.280658 6.25 0.626861 6.25H5.03941ZM5.01489 8.75H2.50745V17.5H5.01489V8.75Z"
                        fill="#408A13" />
                </svg>
                <div>
                  <span class="club-overview__guarantees-card-title">No hidden ticket fees - ever</span>
                  <p class="club-overview__location-description">
                    The price you see is the final price you pay. Unlike other ticket sites, we never add surprise fees
                    at checkout.
                  </p>
                </div>
              </div>
              <div class="buy-now__card tic-card buy-now__payment-methods">
                <svg width="20" height="18" viewBox="0 0 20 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="1" y="1.90002" width="18" height="14.2" stroke="#121318" stroke-width="2" />
                  <line x1="20" y1="6.40002" x2="1.89975e-05" y2="6.40002" stroke="#121318" stroke-width="2" />
                  <rect x="3.47833" y="11.7001" width="1.73913" height="1.8" fill="#121318" />
                  <rect x="6.95654" y="11.7001" width="1.73913" height="1.8" fill="#121318" />
                </svg>
                <div>
                  <span class="buy-now__payment-methods-title">{{ $t('string.Secure-Payments') }}</span>
                  <ul class="buy-now__payment-methods-list">
                    <li class="buy-now__payment-methods-item">
                      <img src="/images/payments/Amex.png" alt="Amex">
                    </li>
                    <li class="buy-now__payment-methods-item">
                      <img src="/images/payments/Visa.png" alt="Visa">
                    </li>
                    <li class="buy-now__payment-methods-item">
                      <img src="/images/payments/Mastercard.png" alt="Mastercard">
                    </li>
                    <li class="buy-now__payment-methods-item">
                      <img src="/images/payments/klarna.png" alt="Klarna">
                    </li>
                    <li class="buy-now__payment-methods-item">
                      <img src="/images/payments/apple.png" alt="apple">
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <div>
              <AppButton
                full-width
                :loading="loading"
                @click="
              async () => {
                loading = true

                await cart.addToCart({
                  productId: product.id,
                  ticketCategoryId: selectedTicketCategory.id,
                  variations: [{ quantity: selectedTicketCount.value }],
                  openHotels: mandatoryHotel,
                }).finally(() => {
                  loading = false
                })


              }
            "
              >
                <span class="only-desktop">{{ mandatoryHotel ? $t('page.product.choose-hotel') : $t('modal.add-to-cart.button') }}</span>
                <span class="only-mobile">{{ mandatoryHotel ? $t('page.product.choose-hotel') : $t('component.tickets.attraction.button') }}</span>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <g clip-path="url(#clip0_5680_796)">
                    <path d="M9.56982 3.63843L15.7586 10.0045L9.56982 16.3706" stroke="white" stroke-width="2"
                          stroke-miterlimit="10" />
                    <path d="M15.2 10L2 10" stroke="white" stroke-width="2" stroke-miterlimit="10" />
                  </g>
                  <defs>
                    <clipPath id="clip0_5680_796">
                      <rect width="15" height="14" fill="white" transform="translate(2 3)" />
                    </clipPath>
                  </defs>
                </svg>
              </AppButton>
            </div>
          </div>
        </div>
      </div>
    </section>

    <div class="tic-container">
      <AppDivider class="my-8 md:my-16 hidden lg:block" strong />
      <section class="more-info section section_border-line">
        <h2 class="more-info__title section__title">more information</h2>
        <div class="more-info__content">
          <UiAccordion v-if="faqs.length" :items="faqs" />
        </div>
      </section>
      <template v-if="product.footballClubPages" v-for="page in product.footballClubPages" :key="page.id">
        <section class="section section_border-line">
          <h2 class="about-club__title section__title">
            {{ $t('page.category-football.products').toUpperCase() }}
          </h2>

          <MatchList style="border-top: unset; padding: 0" v-if="productsByPage[page.id]?.status === 'success'"
                     :title="false" :filters="false" :products="productsByPage[page.id]?.data?.items"
                     :link-query="viewQuery" />
        </section>
        <ClubInfoSection title="">
          <div class="cms-content" v-html="product.intro ?? page.description"></div>
        </ClubInfoSection>

      </template>
      <AppDivider class="my-8 md:my-16" strong />
      <AppNewsletter />
    </div>
    <TicketDetailModal v-model="showModal" :title="$t('string.Ticket-Details')">
      <div v-if="selectedTicketCategory?.keyFeatureLabels?.length" class="ticket-details__labels">
        <KeyLabelFeature
          v-for="(label, i) in selectedTicketCategory.keyFeatureLabels"
          :key="'modal-label-' + i"
          :name="label.name"
          :colour="label.colour"
        />
      </div>
      <div v-html="selectedTicketCategory.description"></div>
    </TicketDetailModal>
  </div>
</template>

<style scoped lang="scss">
@import '@boats/ui/styles/config';

/* Storefront view (BOAT-1912): partner lock-up sits on the H1 row, right-aligned,
   and drops under the title (left-aligned) when the row is too narrow. */
.event-info-card__title-row {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px 24px;

  .event-info-card__title {
    flex: 1 1 280px;
    min-width: 0;
  }
}
.event-info-card__partner-logo {
  display: block;
  flex: none;
  max-height: 56px;
  max-width: 200px;
  object-fit: contain;

  @include media-breakpoint-up(lg) {
    max-height: 72px;
    max-width: 240px;
    margin-top: 4px;
  }
}
.event-info__view-banners {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 12px 12px 16px;

  @include media-breakpoint-up(lg) {
    padding: 16px;
  }
}

.buy-now__header-labels,
.ticket-details__labels {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 0;
}

.buy-now__header-labels {
  // The header's spans carry no vertical rhythm of their own, so the chip row
  // needs its own breathing room above the price card.
  margin: 8px 0 16px;
}

.ticket-details__labels {
  margin-bottom: 12px;
}

.event-info-card-link {
  gap: 6px;
  justify-content: space-between;
  width: 100%;

  @include media-breakpoint-up(lg) {
    justify-content: center;
    width: auto;
  }

  .event-info__location {
    display: flex;
    gap: 6px;
  }
}


.buy-now__container {
  .app-button {
    padding: 1rem 1.5rem;
    bottom: 14px;
  }

  :deep(.app-button__text) {
    display: inline-flex;
  }
}

.tic-info-tag {
  @include label-tag-2;
  display: inline-block;
  align-content: center;
  width: fit-content;
  padding: 0 6px;
  border-radius: 90px;
  white-space: nowrap;

  @include media-breakpoint-up(lg) {
    height: 30px;
    padding: 0 13px;
  }

  &.selling-fast {
    background-color: #ffe4e0;;
  }
}


.tic-card {
  padding: 12px;
  border-radius: 4px;
  border: 1px solid $gray-200;
}

.tic-btn {
  @include label-button;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 2px;
  padding: 0;
  outline: none;
  border: none;
  cursor: pointer;

  &:hover {
    transition: all 0.2s linear;
  }

  &.primary {
    color: $white;
    background-color: $red;

    &:hover {
      background-color: $tint-red-hover;
    }
  }

  &.secondary {
    color: $black;
    background-color: $white;

    &:hover {
      color: $white;
      background-color: $black;
    }
  }

  &.tertiary {
    color: $black;
    background-color: transparent;
  }

  &.ghost {
    color: $white;
    background-color: $gray-300;
  }
}

.event-info-competition {
  padding-bottom: 10px;
  font-family: Poppins, sans-serif;
  font-weight: 500;
  font-size: 12px;
  color: #6d6c6c;

  @include media-breakpoint-up(lg) {
    font-size: 16px;
  }
}

.section {
  padding: 14px 0 40px 0;
  display: grid;
  grid-template-columns: 1fr;

  @include media-breakpoint-up(lg) {
    grid-template-columns: 290px 1fr;
    grid-column-gap: 40px;
    padding: 63px 0;
    border-top: 3px solid $black;
  }

  &_border-line {
    border-top: 2px solid $black;

    @include media-breakpoint-up(lg) {
      border-top: 3px solid $black;
    }
  }

  &__title {
    width: 100%;
    @include h2-barlow;

    @include media-breakpoint-up(lg) {
      width: 270px;
    }
  }
}

.more-info {
  @include media-breakpoint-up(lg) {
    padding-top: 0;
    border-top: none;
  }


  &__title {
    margin-bottom: 32px;
  }

  &__read-more {
    display: flex;
    align-items: center;
    margin-top: 20px;
    font-family: Barlow, sans-serif;
    font-weight: 600;
    font-size: 15px;
    line-height: 100%;
    letter-spacing: 1.5px;
    text-transform: uppercase;
    text-decoration: none;
    color: $black;

    & > span {
      margin-right: 12px;
    }

    & > svg {
      width: 26px;
      height: 26px;
    }
  }

  &__item {
    border-top: 1px solid $black;

    &:last-child {
      border-bottom: 1px solid $black;
    }
  }

  &-card {
    padding: 12px 0;

    @include media-breakpoint-up(lg) {
      padding: 24px 0 26px 0;
    }

    &__header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      cursor: pointer;
    }

    &__content {
      @include body-copy-medium;
      color: $gray-400;
    }

    &__title {
      @include h4-poppins;
      color: $black;
    }

    &__button {
      @include no-bg-button;
      padding: 0;

      & > svg {
        width: 36px;
        height: 36px;
      }
    }

    &__video {
      width: 100%;
      height: 260px;

      @include media-breakpoint-up(lg) {
        width: 620px;
        height: 420px;
      }
    }

    &__list {
      list-style-type: disc;
      padding-left: 20px;
    }
  }
}

.tic-container {
  margin: 2.5rem auto;
  max-width: 80rem;
  padding-left: .75rem;
  padding-right: .75rem;
  width: 100%;

  @include media-breakpoint-up(lg) {
    max-width: 83rem;
    margin-bottom: 5rem;
    margin-top: 5rem;
    padding-left: 1.5rem;
    padding-right: 1.5rem;
  }
}

.event-info {
  position: relative;
  display: flex;
  flex-direction: column;
  background: var(--seat-plan-bg-color);

  &.opened {
    @include media-breakpoint-down(lg) {
      height: 100vh;
    }
  }

  @include media-breakpoint-up(lg) {
    flex-direction: row-reverse;
  }

  &__sidebar {
    background: white;
    @include media-breakpoint-up(lg) {
      width: 630px;
      max-height: fit-content;
      border-right: 1px solid #dedede;
    }

    &.opened {
      @include media-breakpoint-up(lg) {
        height: 100vh;
        max-height: 900px;
        position: relative;
      }
    }

    &-header {
      top: 12px;
      background-color: $gray-100;

      @include media-breakpoint-up(lg) {
        position: static;
      }
    }

    & .event-info__sidebar-header {
      & .event-info-card {
        margin: 0;
        border-radius: 0;
      }
    }
  }

  &__map {
    position: sticky;
    top: 0;
    z-index: 1;
    width: 100%;
    height: 330px;
    display: flex;
    flex-direction: column;
    padding: 8px;

    background-color: var(--seat-plan-bg-color);
    @include media-breakpoint-up(lg) {
      height: 100%;
      max-height: 900px;
      position: static;
      flex: 1;
      padding-top: 0;
    }

    &-canvas {
      height: 100%;
      position: relative;
    }

    &-title {
      position: relative;
      left: 50%;
      transform: translateX(-50%);
      font-family: Barlow, sans-serif;
      font-weight: 600;
      font-size: 12px;
      line-height: 100%;
      letter-spacing: 1.5px;
      text-align: center;
      text-transform: uppercase;
      color: $black;
      margin-bottom: 15px;

      @include media-breakpoint-up(lg) {
        position: relative;
        top: 0;
        padding: 8px;
        margin-top: 34px;
        margin-bottom: unset;
        font-size: 19px;
      }
    }
  }

  &-card {
    display: flex;
    flex-direction: column;
    padding: 12px;
    margin: 12px;
    border-radius: 4px;
    background-color: $white;

    &__guarantees {
      display: flex;
      align-items: center;
      column-gap: 7px;
      margin: 0 0 8px 0;

      @include media-breakpoint-up(lg) {
        margin: 0 0 10px 0;
      }

      &-bold-title {
        font-weight: 700;
        text-transform: uppercase;
      }

      &-percent {
        font-size: 50%;
        vertical-align: super;
      }

      & > svg {
        width: 11px;
        height: 13px;

        @include media-breakpoint-up(lg) {
          width: 17px;
          height: 19px;
        }
      }
    }

    &__title {
      @include h4-poppins;
      margin-bottom: 8px;
      color: $black;

      @include media-breakpoint-up(lg) {
        @include h3-poppins;
        margin-bottom: 10px;
      }
    }

    &__time-and-location {
      display: flex;
      grid-row-gap: 8px;
      flex-direction: column;

      @include media-breakpoint-up(lg) {
        flex-direction: row;
      }

      & > div:first-child {
        display: flex;
        grid-row-gap: 8px;
      }

      &-field {
        display: flex;
        align-items: center;
        column-gap: 5px;
        color: $gray-400;

        & svg {
          width: 14px;
          height: 14px;
          display: unset;

          & > path {
            fill: $gray-400;
          }
        }

        @include media-breakpoint-up(lg) {
          column-gap: 12px;
        }
      }

      .field-1 {
        padding-right: 12px;
        border-right: 1px solid $gray-200;

        @include media-breakpoint-up(lg) {
          padding-right: 17px;
        }
      }

      .field-2 {
        padding: 0 12px;

        @include media-breakpoint-up(lg) {
          padding: 0 17px;
          border-right: 1px solid $gray-200;
        }
      }

      .field-3 {
        justify-content: space-between;
        column-gap: 0;

        & > div {
          display: flex;
          align-items: center;
          justify-content: space-between;
          column-gap: 5px;
        }

        @include media-breakpoint-up(lg) {
          padding-left: 17px;
        }
      }

      &-text {
        @include label-small;
      }
    }

    &-link {
      display: flex;
      align-items: center;
    }
  }

  &__tickets {
    position: relative;

    @include media-breakpoint-up(lg) {
      margin-top: 0;
    }

    &-list {
      @include media-breakpoint-up(lg) {
        max-height: 576px;
        overflow-y: auto;
      }
    }

    &-filters {
      display: flex;
      justify-content: space-between;
      padding: 12px 12px 16px 12px;
      border-bottom: 1px solid $gray-200;
      border-top: 1px solid #DEDEDE;

      & button {
        column-gap: 4px;
      }

      @include media-breakpoint-up(lg) {
        padding: 20px 16px 16px 16px;
      }
    }
  }

  &-tickets {
    border-top: 1px solid $gray-200;

    &-card {
      display: flex;
      height: max-content;
      min-height: 124px;
      max-height: max-content;
      cursor: pointer;

      @include media-breakpoint-up(lg) {
        height: 158px;
      }

      &:hover, &.pinned-category, &.is-hovered {
        & .event-info-tickets-card-color-indicator {
          transition: all 300ms ease;
          width: 10px;
        }

        & .event-info-tickets-card__buy-button {
          transition: all 300ms ease;
          background-color: $tint-red-hover;

          &:disabled {
            background-color: $gray-200;
            color: $gray-300;
          }
        }
      }

      &:last-child {
        border-bottom: 1px solid $gray-200;
      }

      &-color-indicator {
        width: 5px;
      }

      &__info {
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        flex: 1;
        gap: 4px;
        padding: 8px;
        border-right: 1px solid $gray-200;

        @include media-breakpoint-up(lg) {
          gap: 8px;
          padding: 12px;
        }

        & > div {
          display: flex;
          flex-direction: column;
          row-gap: 4px;

          @include media-breakpoint-up(lg) {
            row-gap: 8px;
          }

          &:last-child {
            flex-direction: row;
            justify-content: space-between;
          }

          & > .event-info-tickets-card__location-wrapper {
            display: flex;
            column-gap: 14px;
          }

          & > .event-info-tickets-card__labels {
            display: flex;
            flex-wrap: wrap;
            flex-direction: row;
            gap: 4px 0;
          }
        }
      }

      &__title {
        font-family: Poppins, sans-serif;
        font-weight: 500;
        font-size: 17px;
        line-height: 22px;
        letter-spacing: 0px;
        color: $black;
        display: -webkit-box;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 3;
        overflow: hidden;
        text-overflow: ellipsis;

        @include media-breakpoint-up(lg) {
          font-size: 20px;
          line-height: 32px;
        }
      }

      &__location {
        @include label-small;
        display: flex;
        column-gap: 9px;

        &.seated-together {
          display: none;

          @include media-breakpoint-up(lg) {
            display: flex;
          }
        }

        & > svg {
          width: 20px;
          height: 14px;
        }
      }

      &__details-button {
        @include no-bg-button;
        display: flex;
        align-items: center;
        column-gap: 8px;
        padding: 0;

        & > span {
          @include label-small;
          font-weight: 500;
          font-size: 12px;
          letter-spacing: 1.5px;
          line-height: 20px;
          text-transform: uppercase;

          @include media-breakpoint-up(xs) {
            font-size: 15px;
          }

          @include media-breakpoint-up(lg) {
            line-height: 100%;
          }
        }

        & > svg {
          width: 24px;
          height: 24px;

          @include media-breakpoint-up(lg) {
            width: 26px;
            height: 26px;
          }
        }

        &:hover {
          & > svg {
            @include smooth-transition;
            fill: $black;

            & path {
              stroke: $white;
            }
          }
        }
      }

      &__buy {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: space-between;
        width: 135px;
        padding: 18px 12px 12px 12px;

        &-prices {
          display: flex;
          align-items: baseline;
          justify-content: center;
          gap: 6px;
        }

        &-price {
          @include h4-poppins;
          text-align: center;
        }

        &-was-price {
          font-family: $font-barlow;
          font-size: 0.875rem;
          line-height: 1.2;
          color: $gray-300;
          text-decoration: line-through;
        }

        &-save {
          margin: 6px 0;
        }

        &-button {
          width: 100%;
          height: 48px;
          color: $white;
          background-color: $black;

          @include media-breakpoint-up(lg) {
            height: 62px;
          }

          &:disabled {
            background-color: $gray-200;
            color: $gray-300;
          }
        }

        @include media-breakpoint-up(lg) {
          width: 188px;
          padding: 24px 18px 12px 18px;
        }
      }
    }
  }
}

.buy-now {
  &__sidebar {
    @include smooth-transition;
    position: fixed;
    top: var(--navbar-offset-height, var(--navbar-height));
    overscroll-behavior: contain;
    transform: translateX(-100%);
    z-index: 1;
    width: 100%;
    height: auto;
    bottom: 0;
    overflow-y: auto;
    padding: 12px;
    background-color: $white;

    @include media-breakpoint-up(lg) {
      position: absolute;
      width: 630px;
      overflow-y: hidden;
      // Let the wheel keep scrolling the page once the inner cards list ends —
      // `contain` (needed for the mobile overlay) traps the scroll here and can
      // leave the add-to-cart button unreachable below the fold.
      overscroll-behavior: auto;
      left: 0;
      top: 0;
      padding: 26px 26px 14px 26px;
    }

    &.opened {
      @include smooth-transition;
      transform: translateX(0);
    }
  }

  &__container {
    position: relative;
    height: 100%;
    display: grid;
    @include media-breakpoint-up(lg) {
      display: flex;
      flex-direction: column;
    }
  }

  &__header {
    margin-bottom: 20px;

    @include media-breakpoint-up(lg) {
      margin-bottom: 32px;
    }

    &-club-vs-club {
      @include poppins-12-16-black;
      line-height: 16px;
      display: block;
      margin-bottom: 4px;

      @include media-breakpoint-up(lg) {
        line-height: 32px;
      }
    }

    &-title {
      @include h4-poppins;
      display: block;
      margin-bottom: 8px;
    }

    &-seats-info {
      @include label-small;
      display: block;
      margin-bottom: 20px;
      color: $black;

      @include media-breakpoint-up(lg) {
        margin-bottom: 32px;
      }
    }
  }

  &__map-canvas {
    position: static;
    height: 330px;
    transform: translateX(0);
    margin-bottom: 12px;
    background-color: $white; /* temporary when no svg */
  }

  &__back-button {
    @include no-bg-button;
    display: flex;
    align-items: center;
    padding: 0;
    column-gap: 8px;
    margin-bottom: 8px;

    @include media-breakpoint-up(lg) {
      column-gap: 12px;
      margin-bottom: 10px;
    }

    & > span {
      @include label-button;
      font-size: 12px;
      line-height: 20px;
    }

    & > svg {
      width: 24px;
      height: 24px;

      @include media-breakpoint-up(lg) {
        width: 26px;
        height: 26px;
      }
    }

    &:hover {
      & > svg {
        fill: $black;

        & path {
          fill: $white;
        }
      }
    }
  }

  &__cards {
    display: flex;
    flex-direction: column;
    row-gap: 12px;
    height: 100%;
    margin-bottom: 28px;

    @include media-breakpoint-up(lg) {
      height: 575px;
      overflow-y: scroll;
      flex: 1 1 auto;
      min-height: 0;
    }
  }

  &__card {
    padding: 18px;
  }

  &__price {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 16px;

    &-block {
      display: flex;
      flex-direction: column;
      align-items: end;
      row-gap: 4px;
      width: 120px;

      @include media-breakpoint-up(lg) {
        row-gap: 2px;
      }
    }

    &-amounts {
      display: flex;
      align-items: baseline;
      gap: 6px;
    }

    &-was {
      font-family: $font-barlow;
      font-size: 0.875rem;
      color: $gray-300;
      text-decoration: line-through;
    }
  }

  &__services {
    display: flex;
    align-items: center;
    justify-content: left;
    column-gap: 10px;

    @include media-breakpoint-up(lg) {
      column-gap: 21px;
    }

    &-list {
      display: flex;
      flex-direction: column;
      row-gap: 6px;

      @include media-breakpoint-up(lg) {
        row-gap: 20px;
      }
    }

    & > svg {
      width: 18px;
      height: 21px;

      @include media-breakpoint-up(lg) {
        width: 26px;
        height: 21px;
      }
    }

    & > span {
      @include poppins-12-16-black;
    }
  }

  &__ticket-details {
    &-title {
      @include label-large;
      display: block;
      margin-bottom: 8px;

      @include media-breakpoint-up(lg) {
        font-size: 16px;
        line-height: 28px;
      }
    }
  }

  &__guarantees {
    display: flex;
    column-gap: 12px;

    @include media-breakpoint-up(lg) {
      column-gap: 24px;
    }

    & > div > span {
      line-height: 28px;
      text-align: left;
      margin-bottom: 8px;
    }

    & > div span.event-info-card__guarantees-bold-title {
      font-weight: 600;
      line-height: 17px;
    }

    & > svg {
      width: 20px;
      height: 24px;
      flex-shrink: 0;

      @include media-breakpoint-up(lg) {
        width: 22px;
        height: 26px;
      }
    }
  }

  &__payment-methods {
    display: flex;
    column-gap: 11px;
    padding: 14px 12px 20px 12px;

    & > svg {
      margin-top: 5px;
      width: 20px;
      height: 18px;

      @include media-breakpoint-up(lg) {
        width: 23px;
        height: 20px;
      }

    }

    @include media-breakpoint-up(lg) {
      column-gap: 26px;
    }

    &-title {
      display: block;
      margin-bottom: 16px;
      font-family: Poppins, sans-serif;
      font-weight: 500;
      font-size: 14px;
      line-height: 28px;

      @include media-breakpoint-up(lg) {
        font-size: 16px;
        margin-bottom: 20px;
      }
    }

    &-list {
      display: flex;
      align-items: center;
      column-gap: 24px;
    }

    & img {
      width: 39px;
      height: 24px;

      @include media-breakpoint-up(lg) {
        width: 54px;
        height: 32px;
      }
    }
  }

  &__to-order-button {
    position: relative;
    width: 100%;
    height: 54px;

    column-gap: 8px;
    background-color: $red;

    &:hover {
      background-color: $tint-red-hover;
    }

    & > svg {
      display: unset;
      width: 20px;
      height: 20px;
    }

    & > span {
      font-family: Barlow, sans-serif;
      font-weight: 500;
      font-size: 14px;
      line-height: 100%;
      letter-spacing: 1.5px;
      text-align: center;
      text-transform: uppercase;
      color: $white;

    }
  }
}
</style>
